import React, {useMemo, useState} from "react";
import {Link} from "react-router-dom";
import {getEmployeeReadinessIssue, onboardingStatusLabel} from "../lib/onboarding";
import {requestBusinessActivation} from "../services/onboardingService";

const CheckIcon = ({complete}) => (
    <span className={`onboarding-check-icon ${complete ? "is-complete" : ""}`} aria-hidden="true">
        {complete ? "✓" : ""}
    </span>
);

const CheckItem = ({complete, children, action}) => (
    <li className="onboarding-check-item">
        <span className="onboarding-check-copy">
            <CheckIcon complete={complete}/>
            <span>{children}</span>
        </span>
        {action}
    </li>
);

export default function BusinessOnboardingPanel({business, employees = [], onStatusChange}) {
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState("");
    const status = business?.onboardingStatus;
    const onboardingBusiness = !!status;
    const actualEmployees = employees.filter((employee) => employee.id !== "team");
    const readyEmployees = useMemo(
        () => actualEmployees.filter((employee) => !["deactivated", "readonly"].includes(employee.accountStatus) && !getEmployeeReadinessIssue(employee, business)),
        [actualEmployees, business],
    );
    const detailsComplete = !!(
        business?.legalName
        && business?.displayName
        && business?.address
        && business?.countryCode
        && business?.repFirstName
        && business?.repLastName
    );
    const mayRequest = ["SETUP", "CHANGES_REQUESTED"].includes(status) && readyEmployees.length > 0;

    const requestActivation = async () => {
        setSubmitting(true);
        setError("");
        try {
            const result = await requestBusinessActivation(business.businessID);
            onStatusChange?.(result.onboardingStatus);
        } catch (requestError) {
            setError(requestError.message);
        } finally {
            setSubmitting(false);
        }
    };

    if (!business) return null;

    const statusTone = status === "PENDING_REVIEW"
        ? "is-pending"
        : status === "CHANGES_REQUESTED" || status === "REJECTED"
            ? "is-attention"
            : "";

    return (
        <div className="onboarding-panel">
            <style>{`
                .onboarding-panel { margin-bottom: 28px; }
                .onboarding-mode-card {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    gap: 20px;
                    padding: 18px 20px;
                    border: 1px solid #b9e5d5;
                    border-radius: 16px;
                    background: #e8f5f0;
                }
                .onboarding-mode-content { display: flex; align-items: flex-start; gap: 13px; min-width: 0; }
                .onboarding-mode-icon {
                    width: 36px;
                    height: 36px;
                    flex: 0 0 36px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    border-radius: 10px;
                    background: #ffffff;
                    color: #0d7c5f;
                    box-shadow: 0 1px 2px rgba(13, 124, 95, 0.08);
                }
                .onboarding-mode-title { color: #075c47; font-size: 14px; font-weight: 700; line-height: 1.35; }
                .onboarding-mode-description { margin-top: 3px; color: #447568; font-size: 13px; line-height: 1.45; }
                .onboarding-contact-action {
                    min-height: 38px;
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    flex: none;
                    padding: 9px 14px;
                    border: 1px solid #0d7c5f;
                    border-radius: 9px;
                    color: #0d7c5f;
                    background: transparent;
                    font-size: 13px;
                    font-weight: 600;
                    line-height: 1.3;
                    text-align: center;
                    text-decoration: none;
                    transition: background-color 150ms ease, box-shadow 150ms ease;
                }
                .onboarding-contact-action:hover { background: #ffffff; box-shadow: 0 2px 8px rgba(13, 124, 95, 0.08); }
                .onboarding-card {
                    margin-top: 14px;
                    padding: 22px;
                    border: 1px solid #e8e8ee;
                    border-radius: 18px;
                    background: #ffffff;
                }
                .onboarding-card-header { display: flex; align-items: flex-start; justify-content: space-between; gap: 16px; }
                .onboarding-eyebrow {
                    color: #8b8b9e;
                    font-size: 11px;
                    font-weight: 700;
                    line-height: 1.2;
                    letter-spacing: 0.8px;
                    text-transform: uppercase;
                }
                .onboarding-title { margin-top: 4px; color: #1a1a2e; font-size: 20px; font-weight: 700; line-height: 1.25; }
                .onboarding-status {
                    flex: none;
                    padding: 5px 10px;
                    border-radius: 9999px;
                    background: #f0f0f5;
                    color: #6b6b82;
                    font-size: 10px;
                    font-weight: 700;
                    line-height: 1.2;
                    letter-spacing: 0.5px;
                    text-transform: uppercase;
                }
                .onboarding-status.is-pending { background: #fef6e6; color: #a86206; }
                .onboarding-status.is-attention { background: #fef2f2; color: #b91c1c; }
                .onboarding-note {
                    margin-top: 16px;
                    padding: 12px 14px;
                    border: 1px solid #f0deb3;
                    border-radius: 10px;
                    background: #fef6e6;
                    color: #7a4d08;
                    font-size: 13px;
                    line-height: 1.45;
                }
                .onboarding-check-list { display: flex; flex-direction: column; gap: 7px; margin-top: 18px; list-style: none; }
                .onboarding-check-item {
                    min-height: 46px;
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    gap: 14px;
                    padding: 10px 13px;
                    border: 1px solid #eeeeF2;
                    border-radius: 11px;
                    background: #fafafd;
                }
                .onboarding-check-copy { display: flex; align-items: center; gap: 11px; min-width: 0; color: #505067; font-size: 13px; line-height: 1.35; }
                .onboarding-check-icon {
                    width: 22px;
                    height: 22px;
                    flex: 0 0 22px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    border: 1.5px solid #d4d4dd;
                    border-radius: 9999px;
                    color: #0d7c5f;
                    font-size: 12px;
                    font-weight: 700;
                }
                .onboarding-check-icon.is-complete { border-color: #b9e5d5; background: #e8f5f0; }
                .onboarding-row-action { flex: none; color: #0d7c5f; font-size: 12px; font-weight: 600; text-decoration: none; }
                .onboarding-row-action:hover { text-decoration: underline; }
                .onboarding-card-footer { margin-top: 18px; }
                .onboarding-primary-action {
                    min-height: 42px;
                    padding: 10px 17px;
                    border: 1px solid #0d7c5f;
                    border-radius: 10px;
                    background: #0d7c5f;
                    color: #ffffff;
                    font-family: inherit;
                    font-size: 13px;
                    font-weight: 600;
                    line-height: 1.3;
                    cursor: pointer;
                    transition: background-color 150ms ease, border-color 150ms ease, transform 150ms ease;
                }
                .onboarding-primary-action:hover:not(:disabled) { border-color: #09694f; background: #09694f; }
                .onboarding-primary-action:active:not(:disabled) { transform: translateY(1px); }
                .onboarding-primary-action:disabled { cursor: not-allowed; opacity: 0.45; }
                .onboarding-helper, .onboarding-status-copy { margin-top: 8px; color: #77778b; font-size: 12px; line-height: 1.45; }
                .onboarding-status-copy { margin-top: 18px; font-size: 13px; }
                .onboarding-inline-link { color: #0d7c5f; font-weight: 600; text-decoration: none; }
                .onboarding-inline-link:hover { text-decoration: underline; }
                .onboarding-error { margin-top: 10px; color: #b91c1c; font-size: 12px; line-height: 1.4; }
                .onboarding-active-card {
                    margin-top: 14px;
                    padding: 16px 18px;
                    border: 1px solid #b9e5d5;
                    border-radius: 14px;
                    background: #ffffff;
                    color: #505067;
                    font-size: 13px;
                    line-height: 1.45;
                }
                .onboarding-active-card strong { color: #0d7c5f; }
                @media (max-width: 700px) {
                    .onboarding-mode-card { align-items: stretch; flex-direction: column; gap: 14px; }
                    .onboarding-contact-action { width: 100%; }
                    .onboarding-card { padding: 18px; }
                }
                @media (max-width: 480px) {
                    .onboarding-mode-icon { display: none; }
                    .onboarding-card-header { align-items: flex-start; flex-direction: column; gap: 10px; }
                    .onboarding-check-item { align-items: flex-start; }
                    .onboarding-check-copy { align-items: flex-start; }
                }
            `}</style>

            <div className="onboarding-mode-card">
                <div className="onboarding-mode-content">
                    <span className="onboarding-mode-icon" aria-hidden="true">
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M20 7h-9"/><path d="M14 17H5"/><circle cx="17" cy="17" r="3"/><circle cx="7" cy="7" r="3"/>
                        </svg>
                    </span>
                    <div>
                        <p className="onboarding-mode-title">Individual tipping is active by default</p>
                        <p className="onboarding-mode-description">Each payout-ready employee receives their own Tiply link and QR code.</p>
                    </div>
                </div>
                <a href="mailto:team@tiply.ee?subject=Tiply%20team%20tipping" className="onboarding-contact-action">
                    Ask about shift/hour-weighted team tipping
                </a>
            </div>

            {onboardingBusiness && status !== "ACTIVE" && (
                <section className="onboarding-card">
                    <div className="onboarding-card-header">
                        <div>
                            <p className="onboarding-eyebrow">Business onboarding</p>
                            <h2 className="onboarding-title">{onboardingStatusLabel(status)}</h2>
                        </div>
                        <span className={`onboarding-status ${statusTone}`}>{status.replaceAll("_", " ")}</span>
                    </div>

                    {business?.onboarding?.reviewNote && (
                        <div className="onboarding-note">
                            <strong>Tiply’s note:</strong> {business.onboarding.reviewNote}
                        </div>
                    )}

                    <ul className="onboarding-check-list">
                        <CheckItem complete={detailsComplete} action={<Link to="/business/information" className="onboarding-row-action">Review</Link>}>Business details complete</CheckItem>
                        <CheckItem complete={actualEmployees.length > 0} action={<Link to="/business/employees" className="onboarding-row-action">Manage</Link>}>{actualEmployees.length} employee{actualEmployees.length === 1 ? "" : "s"} added</CheckItem>
                        <CheckItem complete={readyEmployees.length > 0} action={<Link to="/business/employees" className="onboarding-row-action">Complete profiles</Link>}>{readyEmployees.length} employee{readyEmployees.length === 1 ? "" : "s"} payout-ready</CheckItem>
                        <CheckItem complete={status === "PENDING_REVIEW"}>Activation request sent</CheckItem>
                    </ul>

                    {status === "PENDING_REVIEW" ? (
                        <p className="onboarding-status-copy">Tiply is reviewing your business. We’ll email you when the review is complete.</p>
                    ) : status === "REJECTED" ? (
                        <p className="onboarding-status-copy">Contact <a className="onboarding-inline-link" href="mailto:team@tiply.ee">team@tiply.ee</a> if you believe this needs another review.</p>
                    ) : (
                        <div className="onboarding-card-footer">
                            <button type="button" onClick={requestActivation} disabled={!mayRequest || submitting} className="onboarding-primary-action">
                                {submitting ? "Sending request…" : "Request Tiply activation"}
                            </button>
                            {!readyEmployees.length && <p className="onboarding-helper">Complete one employee’s legal name, IBAN, and payout role to enable this button.</p>}
                        </div>
                    )}
                    {error && <p className="onboarding-error">{error}</p>}
                </section>
            )}

            {onboardingBusiness && status === "ACTIVE" && (
                <div className="onboarding-active-card"><strong>Business active.</strong> Payout-ready employees can receive tips. You can add and activate more employees at any time.</div>
            )}
        </div>
    );
}
